<template>
  <div>
    <generic-table
      ref="datatable"
      :title="$t('tickets')"
      :columns="columns"
      :column_search="true"
      resource_url="/helpdesk/ticket/datatables"
      :extra_data="filters"
    >
      <template v-slot:buttons>
        <button class="button is-secondary is-rounded" @click="create_ticket">
          <span class="icon"><i class="fa fa-plus"></i></span>
          <span>{{ $t("add") }}</span>
        </button>
        <button
          class="button is-info is-rounded"
          @click="show_filters = !show_filters"
        >
          <span class="icon"><i class="fa fa-filter"></i></span>
          <span>{{ $t("filters") }}</span>
        </button>
      </template>
    </generic-table>

    <!-- Filtros -->
    <div class="box mt-4" v-if="show_filters">
      <h5 class="title is-6">{{ $t("filters") }}</h5>
      <div class="columns">
        <div class="column">
          <generic-dropdown
            :title="$t('category')"
            :placeholder="$t('select_category')"
            :is_multiple="false"
            url="/helpdesk/category/catalog"
            name_field="name"
            value_field="category_id"
            @selected="filter_by_category"
            ref="category_filter"
          />
        </div>
        <div class="column">
          <generic-dropdown
            :title="$t('status')"
            :placeholder="$t('select_status')"
            :is_multiple="false"
            url="/helpdesk/status/catalog"
            name_field="name"
            value_field="status_id"
            @selected="filter_by_status"
            ref="status_filter"
          />
        </div>
        <div class="column">
          <generic-dropdown
            :title="$t('priority')"
            :placeholder="$t('select_priority')"
            :is_multiple="false"
            url="/helpdesk/priority/catalog"
            name_field="name"
            value_field="priority_id"
            @selected="filter_by_priority"
            ref="priority_filter"
          />
        </div>
        <div class="column">
          <generic-dropdown
            :title="$t('assigned_agent')"
            :placeholder="$t('select_agent')"
            :is_multiple="false"
            url="/helpdesk/agent/catalog"
            name_field="name"
            value_field="agent_id"
            @selected="filter_by_agent"
            ref="agent_filter"
          />
        </div>
      </div>
      <div class="field">
        <button class="button is-light" @click="clear_filters">
          <span class="icon"><i class="fa fa-times"></i></span>
          <span>{{ $t("clear_filters") }}</span>
        </button>
      </div>
    </div>

    <!-- Modal CRUD -->
    <ticket-modal ref="ticket_modal" @success="refresh_datatable" />

    <!-- Modal de Detalles -->
    <ticket-detail-modal ref="ticket_detail_modal" @edit="edit_ticket" />
  </div>
</template>

<script>
import GenericTable from "@/components/generic_table/GenericTable.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import {
  generate_datatables_buttons,
  confirm_deletion_dialog,
  del,
  error_message,
} from "@/utils";
import TicketModal from "./ticket_modal/Index.vue";
import TicketDetailModal from "./ticket_detail_modal/Index.vue";

export default {
  name: "TicketsDataTable",
  components: {
    GenericTable,
    GenericDropdown,
    "ticket-modal": TicketModal,
    "ticket-detail-modal": TicketDetailModal,
  },
  data() {
    return {
      show_filters: false,
      filters: {},
      columns: [
        {
          title: this.$t("ticket_number"),
          name: "ticket_number",
          searchable: true,
          width: "15%",
          data: (row) => {
            return `<span class="tag is-info">${row.ticket_number}</span>`;
          },
        },
        {
          title: this.$t("title"),
          name: "title",
          searchable: true,
          width: "25%",
          data: (row) => row.title || "",
        },
        {
          title: this.$t("customer"),
          width: "15%",
          data: (row) => {
            return row.customer ? row.customer.name : this.$t("no_customer");
          },
        },
        {
          title: this.$t("category"),
          width: "10%",
          data: (row) => {
            if (row.category) {
              return `<span class="tag" style="background-color: ${row.category.color}; color: white;">${row.category.name}</span>`;
            }
            return "-";
          },
        },
        {
          title: this.$t("status"),
          width: "10%",
          data: (row) => {
            if (row.status) {
              return `<span class="tag" style="background-color: ${row.status.color}; color: white;">${row.status.name}</span>`;
            }
            return "-";
          },
        },
        {
          title: this.$t("priority"),
          width: "10%",
          data: (row) => {
            if (row.priority) {
              return `<span class="tag" style="background-color: ${row.priority.color}; color: white;">${row.priority.name}</span>`;
            }
            return "-";
          },
        },
        {
          title: this.$t("assigned_agent"),
          width: "15%",
          data: (row) => {
            return row.assigned_agent
              ? row.assigned_agent.name
              : this.$t("no_agent_assigned");
          },
        },
        {
          title: this.$t("create_date"),
          width: "12%",
          data: (row) => {
            return row.create_date
              ? new Date(row.create_date).toLocaleDateString()
              : "";
          },
        },
        {
          title: this.$t("actions"),
          width: "15%",
          data: (row) => {
            let v = this;
            let btns = [
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("view"),
                icon: "fa fa-eye",
                icon_class: "is-small has-text-info",
                click: function (e) {
                  v.$refs.ticket_detail_modal.show_modal(e.detail.uuid);
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("edit"),
                icon: "fa fa-edit",
                icon_class: "is-small",
                click: function (e) {
                  v.edit_ticket(e.detail.uuid);
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("delete"),
                icon: "fa fa-trash",
                icon_class: "is-small has-text-danger",
                click: (e) => {
                  v.delete_ticket(e.detail.uuid);
                },
              },
            ];
            return generate_datatables_buttons(btns);
          },
        },
      ],
    };
  },
  methods: {
    create_ticket() {
      this.$refs.ticket_modal.show_modal();
    },
    edit_ticket(uuid) {
      this.$refs.ticket_modal.show_modal(uuid);
    },
    delete_ticket(uuid) {
      let v = this;
      confirm_deletion_dialog(v.$t("are_you_sure_delete"), v, () => {
        del("/helpdesk/ticket", { uuid: uuid })
          .then(() => {
            v.refresh_datatable();
          })
          .catch((error) => {
            error_message(error);
          });
      });
    },
    refresh_datatable() {
      this.$refs.datatable.refresh();
    },
    filter_by_category(category) {
      if (category) {
        this.filters.category_id = category.category_id;
      } else {
        delete this.filters.category_id;
      }
      this.apply_filters();
    },
    filter_by_status(status) {
      if (status) {
        this.filters.status_id = status.status_id;
      } else {
        delete this.filters.status_id;
      }
      this.apply_filters();
    },
    filter_by_priority(priority) {
      if (priority) {
        this.filters.priority_id = priority.priority_id;
      } else {
        delete this.filters.priority_id;
      }
      this.apply_filters();
    },
    filter_by_agent(agent) {
      if (agent) {
        this.filters.assigned_agent_id = agent.agent_id;
      } else {
        delete this.filters.assigned_agent_id;
      }
      this.apply_filters();
    },
    apply_filters() {
      this.$refs.datatable.refresh();
    },
    clear_filters() {
      this.filters = {};
      this.$refs.category_filter.clear();
      this.$refs.status_filter.clear();
      this.$refs.priority_filter.clear();
      this.$refs.agent_filter.clear();
      this.$refs.datatable.refresh();
    },
  },
};
</script>
